/**
 * Utility to get client IP address
 * Uses a free IP service to fetch the client's public IP address
 */

let cachedIp: string | null = null;
let ipFetchPromise: Promise<string> | null = null;

/**
 * Fetches the client's public IP address
 * Uses ipify.org as a free service to get the IP
 * Falls back to empty string if fetch fails
 */
export async function getClientIp(): Promise<string> {
  // Return cached IP if available
  if (cachedIp) {
    return cachedIp;
  }

  // Return existing promise if already fetching
  if (ipFetchPromise) {
    return ipFetchPromise;
  }

  // Create new fetch promise
  ipFetchPromise = (async () => {
    try {
      // Try ipify.org first (free, no API key required)
      const response = await fetch('https://api.ipify.org?format=json', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const ip = data.ip || '';
        if (ip) {
          cachedIp = ip;
          return ip;
        }
      }
    } catch (error) {
      // Silently handle fetch error
    }

    // Fallback: try alternative service
    try {
      const response = await fetch('https://api.ipapi.com/api/check?access_key=free', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const ip = data.ip || '';
        if (ip) {
          cachedIp = ip;
          return ip;
        }
      }
    } catch (error) {
      // Silently handle fetch error
    }

    // Final fallback: return empty string
    // Backend should extract IP from request headers if needed
    return '';
  })();

  const ip = await ipFetchPromise;
  return ip;
}

/**
 * Synchronously get cached IP (returns empty string if not cached)
 * Use this when you need the IP immediately without async
 */
export function getCachedClientIp(): string {
  return cachedIp || '';
}

/**
 * Clear the cached IP (useful for testing or when IP might change)
 */
export function clearCachedIp(): void {
  cachedIp = null;
  ipFetchPromise = null;
}

